// ============================================================
// App.jsx
// Componente raíz de la aplicación: estructura del formulario
// completo de cotización de impresión 3D
// ============================================================

import React from 'react';
import { useCalculator } from './hooks/useCalculator';
import { PRINTERS_CATALOG, CURRENCIES, FILAMENT_TYPES } from './data/constants';
import {
  FormField,
  FormSelect,
  SectionCard,
  NeonButton,
  Badge,
  NeonDivider,
} from './components/UI';
import { ResultsPanel }   from './components/ResultsPanel';
import { WhatsAppPanel }  from './components/WhatsAppPanel';

// ---- Estilos del contenedor principal ----
const appContainerStyle = {
  minHeight: '100vh',
  padding: '0 0 60px',
};

const headerStyle = {
  background: 'linear-gradient(180deg, rgba(255,107,0,0.08) 0%, rgba(0,0,0,0) 100%)',
  borderBottom: '1px solid rgba(255,107,0,0.2)',
  padding: '32px 24px 28px',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
};

const contentWrapperStyle = {
  maxWidth: '900px',
  margin: '0 auto',
  padding: '32px 20px 0',
};

export default function App() {
  const calc = useCalculator();

  // ---- Opciones para el select de impresoras ----
  const printerOptions = PRINTERS_CATALOG.map(p => ({
    value: p.id,
    label: p.id === 'custom'
      ? '⚙️ Impresora personalizada'
      : `${p.brand} ${p.model} (${p.powerWatts}W)`,
  }));

  // ---- Opciones para el select de monedas ----
  const currencyOptions = CURRENCIES.map(c => ({
    value: c.code,
    label: `${c.flag} ${c.code} — ${c.name} (${c.symbol})`,
  }));

  // ---- Opciones para tipos de filamento ----
  const filamentOptions = FILAMENT_TYPES.map(f => ({
    value: f.id,
    label: `${f.name} — ${f.description}`,
  }));

  return (
    <div style={appContainerStyle}>
      {/* Efecto scanline decorativo */}
      <div className="scanline-overlay" />

      {/* ============================================
          HEADER: Logo y título
          ============================================ */}
      <header style={headerStyle}>
        {/* Glow decorativo de fondo */}
        <div style={{
          position: 'absolute',
          top: '-60px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '400px',
          height: '150px',
          background: 'radial-gradient(ellipse, rgba(255,107,0,0.25), transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* Icono decorativo */}
        <div style={{
          fontSize: '3rem',
          marginBottom: '12px',
          filter: 'drop-shadow(0 0 20px rgba(255,107,0,0.6))',
        }}>
          🖨️
        </div>

        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'clamp(1.4rem, 4vw, 2.2rem)',
          fontWeight: '900',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: 'var(--neon-orange)',
          textShadow: 'var(--glow-orange)',
          margin: '0 0 8px',
        }}>
          3D Print Cost Calculator
        </h1>

        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '1rem',
          color: 'var(--text-secondary)',
          fontWeight: '400',
          maxWidth: '500px',
          margin: '0 auto 16px',
          lineHeight: '1.5',
        }}>
          Calcula el costo real de cada impresión con desglose profesional,
          márgenes de ganancia y cotización directa por WhatsApp
        </p>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Badge color="orange">Multidivisa</Badge>
          <Badge color="cyan">Desglose total</Badge>
          <Badge color="green">Cotización WhatsApp</Badge>
          <Badge color="pink">Tiempo real</Badge>
        </div>
      </header>

      {/* ============================================
          CONTENIDO PRINCIPAL: Formulario de cálculo
          ============================================ */}
      <main style={contentWrapperStyle}>

        {/* ---- SECCIÓN 1: IMPRESORA ---- */}
        <SectionCard
          title="Impresora 3D"
          icon="🖨️"
          accentColor="var(--neon-cyan)"
          subtitle="Selecciona tu impresora o ingresa la potencia manualmente"
        >
          {/* Full width mediante truco de span */}
          <div style={{ gridColumn: '1 / -1' }}>
            <FormSelect
              label="Modelo de impresora"
              id="selectedPrinter"
              value={calc.form.selectedPrinterId}
              onChange={calc.handlePrinterChange}
              options={printerOptions}
            />

            {/* Info de la impresora seleccionada */}
            {calc.selectedPrinter.id !== 'custom' && (
              <div style={{
                display: 'flex',
                gap: '16px',
                marginTop: '12px',
                flexWrap: 'wrap',
              }}>
                <InfoChip label="Potencia" value={`${calc.selectedPrinter.powerWatts} W`} color="cyan" />
                <InfoChip label="kW" value={`${calc.selectedPrinter.powerKw.toFixed(3)} kW`} color="orange" />
                <InfoChip label="Tipo" value={calc.selectedPrinter.type} color="pink" />
                <InfoChip label="Marca" value={calc.selectedPrinter.brand} color="yellow" />
              </div>
            )}
          </div>

          {/* Campo personalizado: solo visible cuando se selecciona "custom" */}
          {calc.form.selectedPrinterId === 'custom' && (
            <div style={{ gridColumn: '1 / -1' }}>
              <FormField
                label="Potencia de tu impresora"
                id="customPowerWatts"
                type="number"
                value={calc.form.customPowerWatts}
                onChange={v => calc.handleChange('customPowerWatts', v)}
                error={calc.errors.customPowerWatts}
                suffix="W"
                placeholder="Ej: 450"
                min="0"
                step="1"
                hint="Consulta la etiqueta trasera de tu impresora"
                required
              />
              {calc.form.customPowerWatts && (
                <div style={{ marginTop: '8px' }}>
                  <InfoChip
                    label="Equivale a"
                    value={`${(parseFloat(calc.form.customPowerWatts) / 1000).toFixed(3)} kW`}
                    color="cyan"
                  />
                </div>
              )}
            </div>
          )}
        </SectionCard>

        {/* ---- SECCIÓN 2: MONEDA Y ELECTRICIDAD ---- */}
        <SectionCard
          title="Moneda y Electricidad"
          icon="⚡"
          accentColor="var(--neon-yellow)"
          subtitle="Configura tu moneda local y el costo del kWh de tu factura"
        >
          <div style={{ gridColumn: '1 / -1' }}>
            <FormSelect
              label="Moneda"
              id="currency"
              value={calc.form.currencyCode}
              onChange={calc.handleCurrencyChange}
              options={currencyOptions}
              hint={
                calc.form.currencyCode !== 'OTRA'
                  ? `Símbolo: ${calc.selectedCurrency.symbol} | País: ${calc.selectedCurrency.country}`
                  : 'Completa los campos de moneda personalizada abajo'
              }
            />
          </div>

          {/* ---- Campos extra: solo visibles cuando se selecciona "Otra moneda" ---- */}
          {calc.form.currencyCode === 'OTRA' && (
            <>
              <FormField
                label="Nombre de la moneda"
                id="customCurrencyName"
                type="text"
                value={calc.form.customCurrencyName}
                onChange={v => calc.handleChange('customCurrencyName', v)}
                placeholder="Ej: Guaraní, Dinar, Shekel"
                hint="Nombre completo de tu moneda local"
                required
              />
              <FormField
                label="Símbolo"
                id="customCurrencySymbol"
                type="text"
                value={calc.form.customCurrencySymbol}
                onChange={v => calc.handleChange('customCurrencySymbol', v)}
                placeholder="Ej: ₲  £  ₪  Fr"
                hint="Símbolo o abreviación de la moneda"
                required
              />
              <FormField
                label="Decimales"
                id="customCurrencyDecimals"
                type="number"
                value={calc.form.customCurrencyDecimals}
                onChange={v => calc.handleChange('customCurrencyDecimals', v)}
                placeholder="2"
                min="0"
                max="4"
                hint="Normalmente 0 ó 2 decimales"
              />
            </>
          )}

          <div style={{ gridColumn: '1 / -1' }}>
            <FormField
              label={`Precio por kWh (en ${calc.form.currencyCode === 'OTRA' ? (calc.form.customCurrencyName || 'tu moneda') : calc.form.currencyCode})`}
              id="kwPrice"
              type="number"
              value={calc.form.kwPrice}
              onChange={v => calc.handleChange('kwPrice', v)}
              error={calc.errors.kwPrice}
              prefix={calc.selectedCurrency.symbol}
              suffix="/ kWh"
              placeholder="Ej: 740.25"
              min="0"
              step="0.01"
              hint="Encuéntralo en tu factura de servicios públicos"
              required
            />
          </div>

          {/* Referencia de la factura Enel */}
          {calc.form.currencyCode === 'COP' && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{
                background: 'rgba(255, 229, 0, 0.06)',
                border: '1px solid rgba(255, 229, 0, 0.25)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 14px',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.72rem',
                color: 'var(--neon-yellow)',
              }}>
                💡 Referencia Enel Bogotá (ENE/2026): <strong>$740.2485/kWh</strong> — Estrato 3
              </div>
            </div>
          )}
        </SectionCard>

        {/* ---- SECCIÓN 3: TIEMPO DE IMPRESIÓN ---- */}
        <SectionCard
          title="Tiempo de Impresión"
          icon="⏱️"
          accentColor="var(--neon-pink)"
          subtitle="Ingresa el tiempo del slicer. Ej: Creality Slicer muestra 1h 56m"
        >
          <FormField
            label="Horas"
            id="printHours"
            type="number"
            value={calc.form.printHours}
            onChange={v => calc.handleChange('printHours', v)}
            error={calc.errors.printHours}
            suffix="h"
            placeholder="1"
            min="0"
            step="1"
          />
          <FormField
            label="Minutos"
            id="printMinutes"
            type="number"
            value={calc.form.printMinutes}
            onChange={v => calc.handleChange('printMinutes', v)}
            error={calc.errors.printMinutes}
            suffix="min"
            placeholder="56"
            min="0"
            max="59"
            step="1"
          />

          {/* Total en decimal */}
          {calc.totalHours > 0 && (
            <div style={{
              background: 'rgba(255, 0, 122, 0.08)',
              border: '1px solid rgba(255, 0, 122, 0.25)',
              borderRadius: 'var(--radius-md)',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.72rem', textTransform: 'uppercase', fontFamily: 'var(--font-body)', fontWeight: '600', letterSpacing: '0.08em' }}>
                Total
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.2rem', color: 'var(--neon-pink)', fontWeight: '700' }}>
                {calc.totalHours.toFixed(4)} h
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--text-secondary)' }}>
                {Math.floor(calc.totalHours)}h {Math.round((calc.totalHours - Math.floor(calc.totalHours)) * 60)}m
              </span>
            </div>
          )}
        </SectionCard>

        {/* ---- SECCIÓN 4: MATERIAL / FILAMENTO ---- */}
        <SectionCard
          title="Material / Filamento"
          icon="🧵"
          accentColor="var(--neon-orange)"
          subtitle="Ingresa el tipo de material, precio por kg y los gramos que muestra el slicer"
        >
          <div style={{ gridColumn: '1 / -1' }}>
            <FormSelect
              label="Tipo de filamento"
              id="filamentType"
              value={calc.form.filamentType}
              onChange={v => calc.handleChange('filamentType', v)}
              options={filamentOptions}
            />
          </div>

          <FormField
            label={`Precio por kg (${calc.form.currencyCode})`}
            id="filamentPricePerKg"
            type="number"
            value={calc.form.filamentPricePerKg}
            onChange={v => calc.handleChange('filamentPricePerKg', v)}
            error={calc.errors.filamentPricePerKg}
            prefix={calc.selectedCurrency.symbol}
            suffix="/ kg"
            placeholder="Ej: 80000"
            min="0"
            step="0.01"
            hint="Precio de compra del rollo de 1kg"
            required
          />

          <FormField
            label="Peso usado (gramos)"
            id="usedGrams"
            type="number"
            value={calc.form.usedGrams}
            onChange={v => calc.handleChange('usedGrams', v)}
            error={calc.errors.usedGrams}
            suffix="g"
            placeholder="Ej: 27.66"
            min="0"
            step="0.01"
            hint="'Filament Wt' en el slicer"
            required
          />

          {/* Cálculo en vivo del costo de material */}
          {calc.form.filamentPricePerKg && calc.form.usedGrams && (
            <div style={{
              background: 'rgba(255, 107, 0, 0.08)',
              border: '1px solid rgba(255, 107, 0, 0.3)',
              borderRadius: 'var(--radius-md)',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.72rem', textTransform: 'uppercase', fontFamily: 'var(--font-body)', fontWeight: '600', letterSpacing: '0.08em' }}>
                Costo material
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.2rem', color: 'var(--neon-orange)', fontWeight: '700' }}>
                {calc.fmt(calc.results.materialCost)}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--text-secondary)' }}>
                {calc.fmt(parseFloat(calc.form.filamentPricePerKg) / 1000)}/g × {calc.form.usedGrams}g
              </span>
            </div>
          )}
        </SectionCard>

        {/* ---- SECCIÓN 5: COSTOS ADICIONALES (OPCIONAL) ---- */}
        <SectionCard
          title="Costos Adicionales"
          icon="🔧"
          accentColor="var(--neon-cyan)"
          subtitle="Opcional: mantenimiento, depreciación de la máquina y mano de obra"
        >
          <FormField
            label={`Mantenimiento / hora (${calc.form.currencyCode})`}
            id="maintenancePerHour"
            type="number"
            value={calc.form.maintenancePerHour}
            onChange={v => calc.handleChange('maintenancePerHour', v)}
            prefix={calc.selectedCurrency.symbol}
            suffix="/ h"
            placeholder="Ej: 500"
            min="0"
            step="0.01"
            hint="Insumos, mantenimiento preventivo, repuestos"
          />
          <FormField
            label={`Depreciación / hora (${calc.form.currencyCode})`}
            id="depreciationPerHour"
            type="number"
            value={calc.form.depreciationPerHour}
            onChange={v => calc.handleChange('depreciationPerHour', v)}
            prefix={calc.selectedCurrency.symbol}
            suffix="/ h"
            placeholder="Ej: 1000"
            min="0"
            step="0.01"
            hint="Amortización del precio de la máquina en el tiempo"
          />
          <FormField
            label={`Mano de obra total (${calc.form.currencyCode})`}
            id="laborCost"
            type="number"
            value={calc.form.laborCost}
            onChange={v => calc.handleChange('laborCost', v)}
            prefix={calc.selectedCurrency.symbol}
            placeholder="Ej: 5000"
            min="0"
            step="0.01"
            hint="Costo fijo de preparación, configuración y post-procesado"
          />
        </SectionCard>

        {/* ---- SECCIÓN 6: MARGEN DE GANANCIA ---- */}
        <SectionCard
          title="Margen de Ganancia"
          icon="💰"
          accentColor="var(--neon-green)"
          subtitle="Porcentaje de ganancia que deseas obtener sobre el costo total"
        >
          <FormField
            label="Margen de ganancia"
            id="marginPercent"
            type="number"
            value={calc.form.marginPercent}
            onChange={v => calc.handleChange('marginPercent', v)}
            error={calc.errors.marginPercent}
            suffix="%"
            placeholder="Ej: 30"
            min="0"
            step="1"
            hint="Porcentaje sobre el costo total. Ej: 30% = +30% al costo"
          />

          {/* Sugerencias rápidas de margen */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{
              fontFamily: 'var(--font-body)',
              fontWeight: '600',
              fontSize: '0.72rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-secondary)',
            }}>
              Presets rápidos
            </label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[10, 20, 30, 50, 100].map(pct => (
                <button
                  key={pct}
                  onClick={() => calc.handleChange('marginPercent', String(pct))}
                  style={{
                    background: calc.form.marginPercent === String(pct)
                      ? 'rgba(57, 255, 20, 0.25)'
                      : 'rgba(57, 255, 20, 0.07)',
                    border: `1px solid rgba(57, 255, 20, ${calc.form.marginPercent === String(pct) ? '0.6' : '0.2'})`,
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--neon-green)',
                    padding: '5px 12px',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.8rem',
                    fontWeight: '700',
                    transition: 'all 0.15s',
                  }}
                >
                  {pct}%
                </button>
              ))}
            </div>
          </div>
        </SectionCard>

        {/* ---- BOTÓN CALCULAR ---- */}
        <div style={{ marginBottom: '28px' }}>
          <NeonDivider color="var(--neon-orange)" />
          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '20px',
            flexWrap: 'wrap',
          }}>
            <NeonButton
              variant="primary"
              onClick={calc.handleCalculate}
              fullWidth={false}
              icon="⚡"
            >
              Calcular Costo
            </NeonButton>
            <NeonButton
              variant="danger"
              onClick={calc.handleReset}
              icon="↺"
            >
              Reiniciar
            </NeonButton>
          </div>

          {/* Errores de validación globales */}
          {Object.keys(calc.errors).length > 0 && (
            <div style={{
              marginTop: '12px',
              background: 'rgba(255, 68, 68, 0.08)',
              border: '1px solid rgba(255, 68, 68, 0.3)',
              borderRadius: 'var(--radius-md)',
              padding: '12px 16px',
            }}>
              <p style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.75rem',
                color: '#FF6666',
                marginBottom: '6px',
              }}>
                ⚠ Corrige los siguientes campos:
              </p>
              {Object.entries(calc.errors).map(([field, msg]) => (
                <p key={field} style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.72rem',
                  color: '#FF4444',
                  margin: '2px 0',
                }}>
                  • {field}: {msg}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* ---- RESULTADOS ---- */}
        {calc.showResults && (
          <>
            <ResultsPanel
              results={calc.results}
              fmt={calc.fmt}
              selectedPrinter={calc.selectedPrinter}
              currency={calc.selectedCurrency}
              form={calc.form}
              totalHours={calc.totalHours}
            />

            <div style={{ marginTop: '20px' }}>
              <WhatsAppPanel
                form={calc.form}
                results={calc.results}
                fmt={calc.fmt}
                selectedPrinter={calc.selectedPrinter}
                currency={calc.selectedCurrency}
                onChange={calc.handleChange}
                totalHours={calc.totalHours}
              />
            </div>
          </>
        )}

        {/* ---- VISTA PREVIA EN TIEMPO REAL (siempre visible) ---- */}
        {!calc.showResults && calc.results.finalPrice > 0 && (
          <div style={{
            background: 'rgba(255, 107, 0, 0.05)',
            border: '1px dashed rgba(255, 107, 0, 0.3)',
            borderRadius: 'var(--radius-lg)',
            padding: '16px 20px',
            textAlign: 'center',
          }}>
            <p style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.72rem',
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: '6px',
            }}>
              Vista previa en tiempo real
            </p>
            <p style={{
              fontFamily: 'var(--font-display)',
              fontSize: '2rem',
              color: 'var(--neon-orange)',
              textShadow: 'var(--glow-orange)',
              margin: 0,
            }}>
              {calc.fmt(calc.results.finalPrice)}
            </p>
            <p style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.7rem',
              color: 'var(--text-secondary)',
              marginTop: '4px',
            }}>
              Clic en "Calcular Costo" para ver el desglose completo
            </p>
          </div>
        )}

      </main>
    </div>
  );
}

// ---- Sub-componente: chip de información de impresora ----
function InfoChip({ label, value, color }) {
  const colorMap = {
    cyan:   { bg: 'rgba(0,212,255,0.1)',   border: 'rgba(0,212,255,0.3)',   text: 'var(--neon-cyan)'   },
    orange: { bg: 'rgba(255,107,0,0.1)',   border: 'rgba(255,107,0,0.3)',   text: 'var(--neon-orange)' },
    pink:   { bg: 'rgba(255,0,122,0.1)',   border: 'rgba(255,0,122,0.3)',   text: 'var(--neon-pink)'   },
    yellow: { bg: 'rgba(255,229,0,0.1)',   border: 'rgba(255,229,0,0.3)',   text: 'var(--neon-yellow)' },
  };
  const c = colorMap[color] || colorMap.cyan;

  return (
    <div style={{
      display: 'inline-flex',
      flexDirection: 'column',
      background: c.bg,
      border: `1px solid ${c.border}`,
      borderRadius: 'var(--radius-sm)',
      padding: '6px 12px',
    }}>
      <span style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.62rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'var(--text-secondary)',
      }}>
        {label}
      </span>
      <span style={{
        fontFamily: 'var(--font-mono)',
        fontSize: '0.9rem',
        fontWeight: '700',
        color: c.text,
      }}>
        {value}
      </span>
    </div>
  );
}
